import { useMemo } from 'react';
import { connect, FormItemComponentProps } from 'react-antd-formutil';
import FieldSelect from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import API from 'utils/API';
import http from 'utils/http';
import withPreload from 'utils/withPreload';

export interface CountryItem {
    country: string;
    country_code: string;
    tel_code: number;
}

const requestCache: {
    countries?: Promise<CountryItem[]>;
} = {};

export function getCountries() {
    return (
        requestCache.countries ||
        (requestCache.countries = http
            .get<CountryItem[]>(API.CUSTOMER.fields.countryUS())
            .then(({ data }) => data)
            .catch(error => {
                delete requestCache.countries;

                throw error;
            }))
    );
}

interface CountrySelectProps extends Omit<FormItemComponentProps, 'children'> {
    disabled?: boolean;
    placeholder?: string;
    countries: CountryItem[];
    line?: boolean;
    label?: string;
    filterList?: string[];
}

const CountrySelect: React.FC<CountrySelectProps> = ({
    name = 'country',
    label = __('Country'),
    line,
    placeholder = __('Please Select'),
    disabled,
    countries,
    filterList,
    $defaultValue,
    className
}) => {
    const filterData = useMemo(() => {
        if (filterList?.length) {
            return filterList.reduce<CountryItem[]>((prev, cur) => {
                const country = countries.find(item => item.country_code === cur);

                if (country) {
                    prev.push(country);
                }

                return prev;
            }, []);
        }

        return countries;
    }, [countries, filterList]);

    return (
        <FormItem
            className={className}
            label={label}
            name={name}
            line={line}
            $defaultValue={$defaultValue}
            required={!disabled}
            validMessage={{ required: __('Please select a country') }}>
            <FieldSelect
                placeholder={placeholder}
                disabled={disabled}
                label={label}
                data={filterData.map(item => ({
                    key: item.country_code,
                    value: item.country_code,
                    label: item.country
                }))}
            />
        </FormItem>
    );
};

export default withPreload<'countries'>(() => getCountries(), {
    prop: 'countries'
})(connect(CountrySelect));
